import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Bed, Utensils, Coffee, Dumbbell, RefreshCw, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { SHIFT_META } from "@/lib/shifts";
import { useAuth } from "@/context/AuthContext";
import { remindersEnabled, scheduleReminders } from "@/lib/reminders";
import { useSubscription } from "@/context/SubscriptionContext";
import ProLock from "@/components/ProLock";
import WeekAgenda from "@/components/WeekAgenda";

function ShiftBadge({ shift }) {
  const { t } = useI18n();
  const meta = SHIFT_META[shift] || SHIFT_META.off;
  const Icon = meta.icon;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold"
      style={{ backgroundColor: `${meta.color}22`, color: meta.color }}>
      <Icon className="h-4 w-4" /> {t(shift)}
    </span>
  );
}

function CoachRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-white/5 last:border-0">
      <div className="h-9 w-9 shrink-0 rounded-lg bg-secondary flex items-center justify-center">
        <Icon className="h-4 w-4 text-foreground" />
      </div>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="text-sm font-medium leading-snug break-words">{value || "—"}</p>
      </div>
    </div>
  );
}

export default function TodayView({ settings }) {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const { isPro } = useSubscription();
  const [today, setToday] = useState(null);
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadToday = useCallback(async () => {
    const { data } = await api.get("/shifts/today");
    setToday(data);
  }, []);

  const loadCoach = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      const { data } = await api.get("/coach", { params: refresh ? { refresh: true } : {} });
      setCoach(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadToday(); }, [loadToday, lang]);
  useEffect(() => { if (isPro) loadCoach(false); }, [loadCoach, lang, isPro]);

  useEffect(() => {
    if (coach && remindersEnabled()) scheduleReminders(coach, t);
  }, [coach, t]);

  const firstName = user?.name ? user.name.split(" ")[0] : "";

  return (
    <div className="space-y-5">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        <p className="text-muted-foreground">{t("hello")}{firstName ? `, ${firstName}` : ""} 👋</p>
        {today && (
          <div className="mt-3 flex items-center gap-3">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground">{t("todaysShift")}</p>
              <div className="mt-1.5" data-testid="today-shift-badge"><ShiftBadge shift={today.today.shift} /></div>
            </div>
            <div className="ml-auto text-right">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">{t("tomorrow")}</p>
              <div className="mt-1.5"><ShiftBadge shift={today.tomorrow.shift} /></div>
            </div>
          </div>
        )}
      </motion.div>

      {isPro ? (
        <>
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}
            className="coach-glow rounded-2xl p-5"
            data-testid="coach-card"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-[#818CF8]" />
                <div>
                  <h3 className="font-display font-bold text-lg leading-none">{t("coachTitle")}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">{t("coachSub")}</p>
                </div>
              </div>
              <button data-testid="coach-refresh-btn" onClick={() => loadCoach(true)} disabled={loading}
                className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center hover:bg-secondary/70 transition-colors">
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              </button>
            </div>

            {loading && !coach ? (
              <p className="mt-6 text-sm text-muted-foreground animate-pulse">{t("coachLoading")}</p>
            ) : coach ? (
              <div className="mt-4">
                {coach.headline && <p className="font-display font-semibold text-[#c7ccff]">{coach.headline}</p>}
                {coach.summary && <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{coach.summary}</p>}
                <div className="mt-3">
                  <CoachRow icon={Bed} label={t("sleepWindow")} value={coach.sleep_window} />
                  <CoachRow icon={Utensils} label={t("mealTiming")} value={coach.meal_timing} />
                  <CoachRow icon={Coffee} label={t("caffeineCutoff")} value={coach.caffeine_cutoff} />
                  <CoachRow icon={Dumbbell} label={t("workoutSlot")} value={coach.workout_slot} />
                </div>
              </div>
            ) : null}
          </motion.div>

          <WeekAgenda />
        </>
      ) : (
        <ProLock title={t("coachTitle")} testid="today-prolock">
          <div className="coach-glow rounded-2xl p-5">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-[#818CF8]" />
              <h3 className="font-display font-bold text-lg">{t("coachTitle")}</h3>
            </div>
            <div className="mt-4">
              <CoachRow icon={Bed} label={t("sleepWindow")} value="23:00 – 07:00" />
              <CoachRow icon={Utensils} label={t("mealTiming")} value="—" />
              <CoachRow icon={Coffee} label={t("caffeineCutoff")} value="14:00" />
              <CoachRow icon={Dumbbell} label={t("workoutSlot")} value="09:00 – 11:00" />
            </div>
          </div>
        </ProLock>
      )}
    </div>
  );
}
